import {Tab} from "@heroui/react";
import {Table, TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {createFileRoute, useLinkProps, useParams} from '@tanstack/react-router'
import React, {Fragment, useEffect, useState} from "react";
import {apiRetrieve} from "../../../../api.ts";
import Content from "../../../../components/Content.tsx";
import NavTabs from "../../../../components/NavTabs.tsx";
import Header from "../../../../Header.tsx";
import {useJwtUser} from "../../../../JWTAuthProvider.tsx";
import {tableFullScreen} from "../../../../players/PlayersView_Players.tsx";
import queryKeys from "../../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/profiles/$player_uid/stats')({
    component:      RouteComponent,
    staticData:     {
        backUrl: "/profiles/$player_uid/dashboard/"
    },
    validateSearch: (s) => {
        return {subpage: s.subpage as string}
    }
})

function RouteComponent() {
    const search = Route.useSearch();
    const params = useParams({strict: false});
    const user = useJwtUser();

    const [pvpSingleStatsSorted, setPvpSingleStatsSorted] = useState([] as any);

    const [sortDescriptor, setSortDescriptor] = useState<{
        pair: any,
        pvp: any,
    }>({
        pair: {
            column:    'wins_percent',
            direction: 'ascending',
        },
        pvp:  null,
    });

    const {href: pairHref} = useLinkProps({
        to:     "/profiles/$player_uid/stats",
        params: {
            player_uid: params.player_uid as string,
        },
        search: {subpage: 'pair'},
    });
    const {href: pvpHref} = useLinkProps({
        to:     "/profiles/$player_uid/stats",
        params: {
            player_uid: params.player_uid as string,
        },
        search: {subpage: 'pvp'},
    });

    const rankingQuery = useQuery({
        queryKey:        queryKeys.player_stats(sortDescriptor),
        queryFn:         async () => apiRetrieve('player-stats' as any, {sort: sortDescriptor}),
        enabled:         !!user?.uid,
        placeholderData: keepPreviousData,
    })

    const pairStats = rankingQuery.data?.pair;
    const singleStats = rankingQuery.data?.single;
    const playersMap = rankingQuery.data?.players;

    const participants = playersMap;

    useEffect(() => {
        if (!singleStats || !user?.uid || !singleStats[user.uid]) {
            return;
        }
        const sorted =
            (Object.keys(participants || {}) as any)
                .map((p2: string) => (
                    user.uid !== p2 ? {
                        p2_uid:      p2,
                        p2_name:     participants[p2]?.name,
                        played:      singleStats[user.uid]?.[p2]?.played || 0,
                        won_percent: (singleStats[user.uid]?.[p2]?.played > 0 ? (singleStats[user.uid]?.[p2]?.won * 100 / singleStats[user.uid]?.[p2]?.played) : 0),
                    } : null
                ))
                .filter((a: any) => !!a)
                .sort((a: any, b: any) => {
                    const wonDiff = b.won_percent - a.won_percent;
                    if (Math.abs(wonDiff) >= 0.001) {
                        return wonDiff;
                    }
                    return b.played - a.played;
                });
        setPvpSingleStatsSorted(sorted);
    }, [singleStats, user?.uid])

    return <>
        <Header />

        <Content className="light:bg-white dark:bg-[#252525] rounded-xl p-4">
            <NavTabs placement="top"
                     variant="solid"
                     selectedKey={search.subpage}
                     classNames={{
                         base:    "w-full justify-end before:content-['Ranking'] sm:before:hidden before:text-2xl relative before:absolute before:left-0 before:top-2",
                         tab:     "",
                         tabList: "sm:w-full"
                     }}>
                <Tab
                    key="pair"
                    href={pairHref}
                    title={
                        <div className="flex flex-col items-center gap-1">
                            <div>Pair</div>
                        </div>
                    }
                >
                    <div className={"overflow-auto"}>
                        <Table removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Pair Ranking"}
                               sortDescriptor={sortDescriptor.pair}
                               onSortChange={(descr) => setSortDescriptor({...sortDescriptor, pair: descr})}>
                            <TableHeader>
                                <TableColumn className="text-center">{''}</TableColumn>
                                <TableColumn className="text-center">{''}</TableColumn>
                                <TableColumn className="text-center" key="played" allowsSorting>Played</TableColumn>
                                <TableColumn className="text-center" key="wins" allowsSorting>Wins</TableColumn>
                                <TableColumn className="text-center" key="wins_percent" allowsSorting>Win %</TableColumn>
                            </TableHeader>
                            <TableBody>
                                {(pairStats || []).map((pairStat: any, idx) => (
                                    <TableRow key={pairStat.pair_ref}>
                                        <TableCell className="text-right flex justify-between" style={{whiteSpace: 'nowrap'}}>
                                            {idx + 1}.
                                        </TableCell>
                                        <TableCell className="text-center" style={{whiteSpace: 'nowrap'}}>
                                            <span>{playersMap[pairStat.p1]?.name} & {playersMap[pairStat.p2]?.name}</span>
                                        </TableCell>
                                        <TableCell className="text-center">{pairStat.played || 0}</TableCell>
                                        <TableCell className="text-center">{pairStat.wins || 0}</TableCell>
                                        <TableCell className="text-center">{pairStat.played ? ((pairStat.wins * 100.0 / pairStat.played) || 0).toFixed(1) + '%' : 'n/a'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Tab>

                <Tab
                    key="pvp"
                    href={pvpHref}
                    title={
                        <div className="flex flex-col items-center gap-1">
                            <div>{'Player vs Player'}</div>
                        </div>
                    }
                >
                    <nav className="scroll">
                        <div className="overflow-auto">
                            <Table removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Selected Player Ranking"}>
                                <TableHeader>
                                    <TableColumn>{''}</TableColumn>
                                    <TableColumn className="text-center">Played</TableColumn>
                                    <TableColumn className="text-center">Win %</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {pvpSingleStatsSorted.map((entry: any) => (
                                        <TableRow key={entry.p2_uid}>
                                            <TableCell className="text-right" style={{whiteSpace: 'nowrap'}}>{entry.p2_name}</TableCell>
                                            <TableCell className="text-center">{entry?.played || 0}</TableCell>
                                            <TableCell className="text-center">{entry?.played > 0 ? (entry?.won_percent || 0).toFixed(2) + '%' : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </nav>

                </Tab>

            </NavTabs>

        </Content>
    </>
}
